import axios from 'axios';
import toast from 'react-hot-toast';

// Use proxy in development (Vite proxies /api to backend), or direct URL if specified
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '/api' : 'http://localhost:5000/api');

// Token getter function - set by Better Auth auth
let getAuthToken = null;

// Set the token getter (called by AuthTokenProvider)
export const setTokenGetter = (getter) => {
  getAuthToken = getter;
};

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token from Better Auth
api.interceptors.request.use(
  async (config) => {
    if (getAuthToken) {
      try {
        const token = await getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get Better Auth token:', error);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // Handle network errors (no response from server)
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error('Network Error:', {
        message: error.message,
        code: error.code,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        }
      });
      const errorMessage = error.message || 'Network Error';
      return Promise.reject(new Error(errorMessage));
    }
    
    const message = error.response?.data?.message || error.response?.data?.error || 'An error occurred';
    
    // Log full error for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        data: error.response?.data,
        message: message,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
    }
    
    if (error.response?.status === 401) {
      // Session expired - redirect to sign in
      console.warn('401 Unauthorized - Session expired');
      window.location.href = '/sign-in';
      toast.error('Session expired. Please sign in again.');
    } else if (error.response?.status === 403) {
      toast.error('You do not have permission to perform this action');
    } else if (error.response?.status >= 500) {
      toast.error(`Server error: ${message}`);
    }
    
    return Promise.reject(error);
  }
);

// Profile API (uses Better Auth)
export const authAPI = {
  // Get current user and studio from profile/status
  getCurrentUser: async () => {
    const response = await api.get('/profile/status');
    // Transform to match old format for backwards compatibility
    if (response.success && response.data.exists) {
      return {
        success: true,
        data: {
          user: response.data.user,
          studio: response.data.studio
        }
      };
    }
    return response;
  },
  // Register business (creates user + studio)
  registerBusiness: (data) => api.post('/profile/register-business', data),
  // Update user profile
  updateCurrentUser: (data) => api.put('/profile/update-user', data),
  // Update email address
  updateEmail: (data) => api.post('/profile/update-email', data),
  // Update basic studio info (name, address, phone, etc.)
  updateStudio: (data) => api.put('/profile/studio', data),
  // Update studio settings (JSON settings like gaps, notifications, etc.)
  updateStudioSettings: (settings) => api.put('/profile/studio/settings', { settings }),
};

// Videos API
export const videosAPI = {
  getVideos: (params) => api.get('/videos', { params }),
  getVideo: (id) => api.get(`/videos/${id}`),
  downloadVideo: (videoId, quality) => api.post('/videos/download', { videoId, quality }),
  getDownloadedVideos: (studioId) => api.get(`/videos/studio/${studioId}/downloads`),
  getCategories: () => api.get('/videos/categories'),
  syncVideo: (videoData) => api.post('/videos/sync', videoData),
  syncVideos: (videos) => api.post('/videos/sync/batch', { videos }),
  updateLocalVideo: (id, data) => api.put(`/videos/local/${id}`, data),
  deleteLocalVideos: () => api.delete('/videos/local'),
  // Thumbnail endpoints (loaded separately for performance)
  getThumbnail: (id) => api.get(`/videos/${id}/thumbnail`),
  getThumbnails: (ids) => api.post('/videos/thumbnails', { ids }),
};

// Schedules API
export const schedulesAPI = {
  getSchedules: (studioId, params) => api.get(`/schedules/studio/${studioId}`, { params }),
  getSchedule: (id) => api.get(`/schedules/${id}`),
  createSchedule: (data) => api.post('/schedules', data),
  updateSchedule: (id, data) => api.put(`/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/schedules/${id}`),
  markAsPlayed: (id) => api.post(`/schedules/${id}/playback`),

  // Recurring Schedules API
  createRecurringSeries: (data) => api.post('/schedules/recurring', data),
  convertToRecurring: (scheduleId, data) => api.post(`/schedules/${scheduleId}/convert-to-recurring`, data),
  getSeries: (seriesId) => api.get(`/schedules/series/${seriesId}`),
  getSeriesInstances: (seriesId, params) => api.get(`/schedules/series/${seriesId}/instances`, { params }),
  updateSeries: (seriesId, data) => api.put(`/schedules/series/${seriesId}`, data),
  updateSeriesFromInstance: (instanceId, data) => api.put(`/schedules/${instanceId}/series`, data),
  deleteSeries: (seriesId) => api.delete(`/schedules/series/${seriesId}`),
  deleteSeriesFromInstance: (instanceId) => api.delete(`/schedules/${instanceId}/series`),
};

// Marketing API
export const marketingAPI = {
  getMaterials: (params) => api.get('/marketing', { params }),
  getMaterial: (id) => api.get(`/marketing/${id}`),
  getCalendar: () => api.get('/marketing/calendar'),
  startCampaign: (data) => api.post('/marketing/campaign/start', data),
  getCategories: () => api.get('/marketing/categories'),
};

// Subscription API
export const subscriptionAPI = {
  getSubscription: (studioId) => api.get(`/subscriptions/${studioId}`),
  getTiers: () => api.get('/subscriptions/tiers'),
  updateTier: (studioId, tier) => api.put(`/subscriptions/${studioId}/tier`, { tier }),
  cancelSubscription: (studioId, data) => api.post(`/subscriptions/${studioId}/cancel`, data),
  getPaymentMethods: (studioId) => api.get(`/subscriptions/${studioId}/payment-methods`),
  addPaymentMethod: (studioId, data) => api.post(`/subscriptions/${studioId}/payment-methods`, data),
  getInvoices: (studioId, params) => api.get(`/subscriptions/${studioId}/invoices`, { params }),
};

// CRM API
export const crmAPI = {
  getCustomers: (studioId, params) => api.get(`/crm/${studioId}/customers`, { params }),
  getCustomer: (id) => api.get(`/crm/customers/${id}`),
  createCustomer: (studioId, data) => api.post(`/crm/${studioId}/customers`, data),
  updateCustomer: (id, data) => api.put(`/crm/customers/${id}`, data),
  deleteCustomer: (id) => api.delete(`/crm/customers/${id}`),
  getMonthlyRevenue: (studioId, params) => api.get(`/crm/${studioId}/revenue`, { params }),
  resendInvite: (studioId, customerId) => api.post(`/crm/${studioId}/customers/${customerId}/resend-invite`),
};

// Bookings API
export const bookingsAPI = {
  createBooking: (data) => api.post('/bookings/create', data),
  cancelBooking: (data) => api.delete('/bookings/cancel', { data }),
  getMyBookings: (studentId) => api.get('/bookings/my-bookings', { params: { studentId } }),
  getClassBookings: (scheduleId) => api.get(`/bookings/class/${scheduleId}`),
  getStudioBookings: (studioId, params = {}) => api.get(`/bookings/studio/${studioId}`, { params }),
};

// Customer Subscriptions API
export const customerSubscriptionsAPI = {
  getSubscription: () => api.get('/customer-subscriptions/me'),
  createCheckoutSession: (data) => api.post('/customer-subscriptions/checkout', data),
  cancelSubscription: () => api.post('/customer-subscriptions/cancel'),
};

export default api;
